import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  legalPage: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '24px',
    boxSizing: 'border-box',
  },
  legalContent: {
    maxWidth: '920px',
    width: '100%',
    lineHeight: '1.5',
    color: tokens.colorNeutralForeground1,
  },
  legalList: {
    marginTop: '8px',
    marginBottom: '16px',
  },
});

export const PrivacyPolicyContent: React.FC = () => {
  const styles = useStyles();

  return (
    <main className={styles.legalPage}>
      <article className={styles.legalContent}>
        <h1>Privacy Policy</h1>
        <p>Last updated: February 28, 2026</p>

        <h2>1. Overview</h2>
        <p>
          This Privacy Policy describes how our application (“the Service”) collects, uses, and
          protects user information when Guides and Customers use our scheduling platform.
        </p>
        <p>
          The Service is an internal operational booking and session-management portal that allows
          customers to book appointments with Guides based on their availability. Guides connect
          their Google Calendar to allow the system to manage scheduling and prevent booking
          conflicts.
        </p>

        <h2>2. Information We Collect</h2>
        <h3>2.1 Guide Information</h3>
        <p>We may collect and store:</p>
        <ul className={styles.legalList}>
          <li>Name</li>
          <li>Email address</li>
          <li>Timezone</li>
          <li>Availability schedule</li>
          <li>Session history related to customer bookings</li>
        </ul>

        <h3>2.2 Customer Information</h3>
        <p>We may collect and store:</p>
        <ul className={styles.legalList}>
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Booking history</li>
          <li>Conversation or session notes created by the Guide</li>
        </ul>
        <p>This information is stored in our database hosted on Firebase.</p>

        <h2>3. Google Calendar Data</h2>
        <p>
          Our application requests permission to access Google Calendar data using OAuth
          authentication.
        </p>
        <p>We access Google Calendar only after explicit user consent.</p>

        <h3>What We Access</h3>
        <p>We access only the minimum calendar information necessary to operate scheduling:</p>
        <ul className={styles.legalList}>
          <li>Free/busy availability</li>
          <li>Ability to create calendar events</li>
          <li>Ability to modify calendar events created by our system</li>
          <li>Ability to delete calendar events created by our system</li>
        </ul>
        <p>We do not read or store:</p>
        <ul className={styles.legalList}>
          <li>event titles</li>
          <li>event descriptions</li>
          <li>attendee lists</li>
          <li>unrelated personal calendar entries</li>
        </ul>

        <h3>How We Use Google Calendar Data</h3>
        <p>Google Calendar data is used strictly for:</p>
        <ul className={styles.legalList}>
          <li>determining Guide availability</li>
          <li>preventing double booking</li>
          <li>blocking time slots during scheduled sessions</li>
        </ul>
        <p>The system creates calendar blocks representing booked sessions.</p>
        <p>We do not use Google Calendar data for:</p>
        <ul className={styles.legalList}>
          <li>advertising</li>
          <li>marketing</li>
          <li>analytics profiling</li>
          <li>AI training</li>
          <li>data resale</li>
        </ul>

        <h2>4. Data Storage</h2>
        <p>We store only limited operational information from Google Calendar:</p>
        <ul className={styles.legalList}>
          <li>the calendar event ID associated with a booking</li>
        </ul>
        <p>We do not store copies of calendar events or personal calendar history.</p>
        <p>
          All OAuth access tokens are securely stored and removed immediately when a user
          disconnects their Google account.
        </p>

        <h2>5. Data Sharing</h2>
        <p>We do not sell, rent, or share user data with third parties.</p>
        <p>
          Google user data is used solely to provide the scheduling functionality of the Service.
        </p>

        <h2>6. Data Retention</h2>
        <h3>Google Account Disconnection</h3>
        <p>If a Guide disconnects their Google account:</p>
        <ul className={styles.legalList}>
          <li>OAuth tokens are permanently deleted</li>
          <li>calendar access stops immediately</li>
          <li>booking history remains for operational recordkeeping</li>
        </ul>

        <h3>Guide Account Deletion</h3>
        <p>If a Guide deletes their account:</p>
        <ul className={styles.legalList}>
          <li>all Guide profile information is deleted</li>
          <li>calendar connection data is deleted</li>
          <li>booking history remains only as part of customer records</li>
        </ul>

        <h3>Customer Data Deletion</h3>
        <p>Upon request, all customer personal data and booking history are permanently deleted.</p>

        <h2>7. User Control and Revocation</h2>
        <p>
          Users may revoke Google Calendar access at any time from their Google Account security
          permissions page.
        </p>
        <p>Once revoked, the Service can no longer access the user’s calendar.</p>

        <h2>8. Third-Party Services</h2>
        <p>
          We use Firebase (Google Cloud) for secure data storage and system operations. These
          services act only as data processors and do not use the information for their own
          purposes.
        </p>

        <h2>9. How to Request Data Deletion</h2>
        <p>Users may request account or data deletion by contacting:</p>
        <p>Email: client@leelalife.com</p>
        <p>Requests are processed manually.</p>

        <h2>10. Security</h2>
        <p>
          We take reasonable technical and organizational measures to protect user data from
          unauthorized access, misuse, or disclosure.
        </p>

        <h2>11. Compliance with Google API Services User Data Policy</h2>
        <p>
          Our use of information received from Google APIs will adhere to the Google API Services
          User Data Policy, including the Limited Use requirements.
        </p>
        <p>
          Google user data is used only to provide or improve user-facing features that are clearly
          described in this policy.
        </p>
      </article>
    </main>
  );
};
